import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useClient } from '../context/ClientContext';
import { Sparkles, Check, Loader2, AlertCircle, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function AIImport({ open, onOpenChange, onImportComplete, currentMonth, currentYear }) {
  const { activeClient } = useClient();
  const { toast } = useToast();

  const [step, setStep] = useState('input');
  const [text, setText] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [year, setYear] = useState(currentYear || new Date().getFullYear());
  const [postsPerMonth, setPostsPerMonth] = useState(12);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null); // { month, monthIndex, totalMonths, runningTotal }
  const abortRef = useRef(null);

  useEffect(() => {
    if (open) {
      const m = currentMonth || new Date().getMonth() + 1;
      setSelectedMonths([m]);
      setYear(currentYear || new Date().getFullYear());
      setStep('input');
      setPreview([]);
      setError('');
      setProgress(null);
    }
    return () => { abortRef.current?.abort(); };
  }, [open, currentMonth, currentYear]);

  const toggleMonth = (m) => {
    setSelectedMonths(prev =>
      prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m].sort((a, b) => a - b)
    );
  };

  const selectAll = () => {
    if (selectedMonths.length === 12) {
      setSelectedMonths([]);
    } else {
      setSelectedMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]);
    }
  };

  const handleGenerate = useCallback(async () => {
    if (!text.trim()) { setError('Paste your content strategy first'); return; }
    if (selectedMonths.length === 0) { setError('Select at least one month'); return; }

    setLoading(true);
    setError('');
    setProgress(null);
    setPreview([]);

    const controller = new AbortController();
    abortRef.current = controller;

    // Build month-year pairs: months past December roll into next year
    const sortedMonths = [...selectedMonths].sort((a, b) => a - b);
    const monthYearPairs = sortedMonths.map(m => ({
      month: m,
      year: m < sortedMonths[0] ? year + 1 : year,
    }));

    try {
      const res = await fetch(`${API}/import/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        signal: controller.signal,
        body: JSON.stringify({
          text: text.trim(),
          month_year_pairs: monthYearPairs,
          num_posts: postsPerMonth,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const collectedPosts = [];
      let monthErrors = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            if (event.type === 'progress') {
              setProgress({
                month: event.month,
                monthIndex: event.month_index,
                totalMonths: event.total_months,
                message: event.message,
                runningTotal: collectedPosts.length,
              });
            } else if (event.type === 'month_complete') {
              collectedPosts.push(...event.posts);
              setPreview([...collectedPosts]);
              setProgress({
                month: event.month,
                monthIndex: event.month_index,
                totalMonths: event.total_months,
                runningTotal: collectedPosts.length,
                message: `${event.month} done — ${event.month_count} posts`,
              });
            } else if (event.type === 'month_error') {
              monthErrors.push(event.month);
              setProgress(prev => ({
                ...prev,
                monthIndex: event.month_index,
                message: `${event.month} failed, continuing...`,
              }));
            } else if (event.type === 'error') {
              throw new Error(event.message);
            } else if (event.type === 'complete') {
              setPreview(event.posts || collectedPosts);
            }
          } catch (parseErr) {
            // skip malformed lines
          }
        }
      }

      if (collectedPosts.length > 0) {
        setStep('preview');
        const successCount = selectedMonths.length - monthErrors.length;
        toast({
          title: `Generated ${collectedPosts.length} posts across ${successCount} month${successCount !== 1 ? 's' : ''}`,
          description: monthErrors.length > 0
            ? `${monthErrors.join(', ')} failed — you can retry those later.`
            : 'Review and confirm to add them as drafts.',
        });
      } else {
        setError('No posts were generated. Please try again with different instructions.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Generation failed. Try again.');
      }
    }
    setLoading(false);
    setProgress(null);
  }, [text, selectedMonths, year, postsPerMonth, toast]);

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/import/ai/confirm`, {
        events: preview,
        year,
      }, { withCredentials: true });

      toast({ title: `${data.count} posts added as drafts!` });
      onImportComplete?.();
      onOpenChange(false);
    } catch (err) {
      setError('Failed to save posts. Try again.');
    }
    setLoading(false);
  }, [preview, year, toast, onImportComplete, onOpenChange]);

  const postsByMonth = preview.reduce((acc, p) => {
    const d = p.scheduled_date || '';
    const m = parseInt(d.split('-')[1], 10);
    if (!acc[m]) acc[m] = [];
    acc[m].push(p);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#003C59]">
            <Sparkles className="w-5 h-5 text-[#01939E]" />
            AI Post Generator
            {activeClient && <span className="text-sm font-normal text-gray-400 ml-2">for {activeClient.name}</span>}
          </DialogTitle>
        </DialogHeader>

        {step === 'input' && (
          <div className="space-y-4 mt-2">
            {/* Month selector */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-[#003C59] uppercase tracking-wide">Months to Generate</label>
                <button
                  onClick={selectAll}
                  className="text-[10px] text-[#01939E] hover:underline font-medium"
                  data-testid="ai-select-all-months"
                >
                  {selectedMonths.length === 12 ? 'Deselect All' : 'Select All 12'}
                </button>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                {MONTHS.map((name, i) => {
                  const m = i + 1;
                  const active = selectedMonths.includes(m);
                  // Show year rollover hint: if this month is before the earliest selected month, it's next year
                  const sortedSel = [...selectedMonths].sort((a, b) => a - b);
                  const isNextYear = sortedSel.length > 0 && m < sortedSel[0] && selectedMonths.includes(m);
                  return (
                    <button
                      key={m}
                      onClick={() => toggleMonth(m)}
                      className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                        active
                          ? 'bg-[#01939E]/10 border-[#01939E] text-[#003C59]'
                          : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                      }`}
                      data-testid={`ai-month-${m}`}
                    >
                      {name.slice(0, 3)}
                      {isNextYear && <span className="text-[8px] text-[#01939E] block">{year + 1}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Year + posts per month */}
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="text-xs font-semibold text-[#003C59] uppercase tracking-wide mb-1 block">Starting Year</label>
                <select
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="ai-year-select"
                >
                  {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-xs font-semibold text-[#003C59] uppercase tracking-wide mb-1 block">Posts per Month</label>
                <select
                  value={postsPerMonth}
                  onChange={(e) => setPostsPerMonth(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="ai-posts-per-month"
                >
                  {[8, 10, 12, 15, 18, 20].map(n => <option key={n} value={n}>{n} posts</option>)}
                </select>
              </div>
            </div>

            {/* Strategy text */}
            <div>
              <label className="text-xs font-semibold text-[#003C59] uppercase tracking-wide mb-1 block">
                Content Strategy / Instructions
              </label>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste your content strategy, themes, post ideas, or instructions for the AI..."
                rows={6}
                className="resize-none text-sm"
                data-testid="ai-strategy-input"
              />
              <p className="text-[10px] text-gray-400 mt-1">
                {selectedMonths.length} month{selectedMonths.length !== 1 ? 's' : ''} selected &middot; {selectedMonths.length * postsPerMonth} total posts will be generated
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {/* Progress indicator during generation */}
            {loading && progress && (
              <div className="space-y-2 bg-[#01939E]/5 rounded-lg p-4" data-testid="ai-progress">
                <div className="flex items-center justify-between text-xs font-medium">
                  <span className="text-[#003C59]">{progress.message}</span>
                  <span className="text-[#01939E]">{progress.monthIndex}/{progress.totalMonths}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-[#01939E] h-full rounded-full transition-all duration-500"
                    style={{ width: `${(progress.monthIndex / progress.totalMonths) * 100}%` }}
                  />
                </div>
                {progress.runningTotal > 0 && (
                  <p className="text-[10px] text-gray-400 text-center">
                    {progress.runningTotal} posts generated so far
                  </p>
                )}
              </div>
            )}

            <button
              onClick={handleGenerate}
              disabled={loading || !text.trim() || selectedMonths.length === 0}
              className="w-full py-2.5 bg-[#01939E] hover:bg-[#017a83] text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="ai-generate-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Generating{progress ? ` — ${progress.month || ''}` : ''}...</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Generate Posts</>
              )}
            </button>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4 mt-2">
            <p className="text-sm text-gray-500">
              {preview.length} posts generated across {Object.keys(postsByMonth).length} month{Object.keys(postsByMonth).length !== 1 ? 's' : ''}. Review below, then confirm to save as drafts.
            </p>

            <div className="max-h-[50vh] overflow-y-auto space-y-4 pr-1">
              {Object.entries(postsByMonth)
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .map(([monthNum, posts]) => (
                  <div key={monthNum}>
                    <h3 className="text-xs font-bold text-[#003C59] uppercase tracking-wide mb-2 sticky top-0 bg-white py-1">
                      {MONTHS[parseInt(monthNum) - 1]} {year} ({posts.length} posts)
                    </h3>
                    <div className="space-y-1.5">
                      {posts.map((post, i) => (
                        <div key={i} className="flex gap-3 px-3 py-2 bg-gray-50 rounded-lg">
                          <div className="text-[10px] text-gray-400 font-mono whitespace-nowrap mt-0.5">
                            {post.scheduled_date?.slice(5)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#003C59] truncate">{post.title}</p>
                            <p className="text-xs text-gray-500 line-clamp-2">{post.content}</p>
                          </div>
                          <span className="text-[10px] text-[#01939E] bg-[#01939E]/10 px-1.5 py-0.5 rounded h-fit shrink-0">
                            {post.post_type}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setStep('input')}
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors text-sm"
                data-testid="ai-back-btn"
              >
                Back to Edit
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className="flex-1 py-2.5 bg-[#01939E] hover:bg-[#017a83] text-white font-semibold rounded-lg transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="ai-confirm-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                ) : (
                  <><Check className="w-4 h-4" /> Confirm {preview.length} Posts as Drafts</>
                )}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
